import React, { useEffect, useRef, useState } from "react";
import { DatePicker, Input, InputNumber, Modal, Segmented, Tooltip } from "antd";
import dayjs from "dayjs";
import { AN_COLORS, AN_FONT } from "../../../design-system/tokens";
import { fmtDate } from "../../../design-system/tableUi";
import { FieldLabel, Note } from "../../../pages/enduser/ssh-proxy/sshProxyUi";
import { createManagedAccount, listLocalUsers, passwordErrorText } from "../../../service/sshProxyService";

// "Add user": a local account Authnull creates on one endpoint. It logs in
// with the key Authnull holds, or with a password that is vaulted and rotated
// on its own schedule; it is removed at its expiry, if it has one. Creating
// one grants nobody access; an Endpoint Policy that names it does.
// Administrators only. Opened from the endpoint details page (its ⋮ menu and
// the Local users table).

const DEFAULT_MIN_HOURS = 1;
const DEFAULT_MAX_DAYS = 90;
const DEFAULT_INTERVAL_DAYS = 30;
const NOTE_MAX = 300;

const muted = { fontSize: 11.5, color: AN_COLORS.muted };
const HOUR = 3600e3;
const DAY = 24 * HOUR;

// The backend's rule (managedaccounts.validName): lower-case letters, digits,
// ".", "_" and "-", at most 32, not starting with "-", and nothing reserved —
// root, authnull-svc, nobody, temp, or a name starting "an-".
const RESERVED = new Set(["root", "authnull-svc", "nobody", "temp"]);
export const managedAccountNameError = (raw) => {
  const a = String(raw || "").trim();
  if (!a) return "Enter a username.";
  if (a.length > 32) return "At most 32 characters.";
  if (a.startsWith("-")) return "It can't start with “-”.";
  if (!/^[a-z0-9._-]+$/.test(a)) return "Use lower-case letters, digits, “.”, “_” and “-” only.";
  if (RESERVED.has(a)) return `“${a}” is reserved.`;
  if (a.startsWith("an-")) return "Names starting with “an-” are reserved.";
  return "";
};

// "in 6 days" / "in 5 hours" / "in 20 minutes" / "expired"
export const relativeTime = (iso) => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const ms = t - Date.now();
  if (ms <= 0) return "expired";
  const plural = (n, unit) => `in ${n} ${unit}${n === 1 ? "" : "s"}`;
  if (ms < HOUR) return plural(Math.max(1, Math.round(ms / 60e3)), "minute");
  if (ms < 2 * DAY) return plural(Math.round(ms / HOUR), "hour");
  return plural(Math.round(ms / DAY), "day");
};

// The add-user limits a listLocalUsers response carries.
export const addUserLimitsOf = (res) => ({
  canAddPermanent: res?.canAddPermanent !== false,
  defaultIntervalDays: Number(res?.defaultIntervalDays) > 0 ? Number(res.defaultIntervalDays) : DEFAULT_INTERVAL_DAYS,
  minHours: Number(res?.minHours) > 0 ? Number(res.minHours) : DEFAULT_MIN_HOURS,
  maxDays: Number(res?.maxDays) > 0 ? Number(res.maxDays) : DEFAULT_MAX_DAYS,
});

const emptyForm = (limits) => ({
  account: "",
  credential: "key",
  intervalDays: limits.defaultIntervalDays,
  expires: limits.canAddPermanent ? "never" : "on",
  expiresAt: dayjs().add(Math.min(7, limits.maxDays), "day"),
  note: "",
});

// `limits` ({ canAddPermanent, defaultIntervalDays, minHours, maxDays }) is
// optional — without it they are read (listLocalUsers) when the form opens.
// `existingAccounts` (optional) are the endpoint's account names, refused
// up front. onCreated(managedAccount | null, account) runs after the create
// is accepted; the caller shows the toast and refreshes.
export function AddManagedAccountModal({ open, instanceId, hostname, limits: rawLimits, existingAccounts, onClose, onCreated }) {
  const [readLimits, setReadLimits] = useState(null);
  const givenLimits = rawLimits ? { ...addUserLimitsOf(null), ...rawLimits } : null;
  const limits = givenLimits || readLimits || addUserLimitsOf(null);
  const [form, setForm] = useState(() => emptyForm(limits));
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState("");

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setForm(emptyForm(givenLimits || addUserLimitsOf(null)));
    setTouched(false);
    setCreateError("");
    setSaving(false);
    if (!givenLimits && instanceId) {
      setReadLimits(null);
      listLocalUsers({
        instanceId: Number(instanceId),
        onSuccess: (res) => {
          if (!live) return;
          const l = addUserLimitsOf(res);
          setReadLimits(l);
          setForm((f) => ({
            ...f,
            intervalDays: l.defaultIntervalDays,
            expires: l.canAddPermanent ? f.expires : "on",
          }));
        },
        onFailure: () => {},
      });
    }
    return () => {
      live = false;
    };
    // givenLimits is read once per open; re-running on its identity would reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, instanceId]);

  const { canAddPermanent, minHours, maxDays } = limits;
  const never = form.expires === "never" && canAddPermanent;
  const account = form.account.trim();

  const nameError =
    managedAccountNameError(account) ||
    ((existingAccounts || []).includes(account) ? `${hostname || "This endpoint"} already has an account “${account}”.` : "");
  const intervalError =
    form.credential === "password" && !(Number.isInteger(form.intervalDays) && form.intervalDays >= 1 && form.intervalDays <= 365)
      ? "Rotate every 1 to 365 days."
      : "";
  const expiryError = (() => {
    if (never) return "";
    if (!form.expiresAt) return "Pick when it expires.";
    const ms = form.expiresAt.valueOf() - Date.now();
    if (ms < minHours * HOUR) return `It must last at least ${minHours} hour${minHours === 1 ? "" : "s"}.`;
    if (ms > maxDays * DAY) return `It can last at most ${maxDays} days.`;
    return "";
  })();
  const noteError = form.note.trim().length > NOTE_MAX ? `At most ${NOTE_MAX} characters.` : "";
  const formInvalid = !!(nameError || intervalError || expiryError || noteError);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setCreateError("");
  };

  const submit = () => {
    setTouched(true);
    if (formInvalid) return;
    setSaving(true);
    setCreateError("");
    createManagedAccount({
      instanceId,
      account,
      credential: form.credential,
      intervalDays: form.intervalDays,
      expiresAt: never ? "" : form.expiresAt.toDate().toISOString(),
      note: form.note.trim(),
      onSuccess: (res) => {
        if (!mounted.current) return;
        setSaving(false);
        onCreated && onCreated(res.managedAccount || null, account);
        onClose && onClose();
      },
      onFailure: (err) => {
        if (!mounted.current) return;
        setSaving(false);
        setCreateError(passwordErrorText(err, "Couldn't add the user."));
      },
    });
  };

  const disabledDate = (d) =>
    d && (d.isBefore(dayjs().startOf("day")) || d.isAfter(dayjs().add(maxDays, "day").endOf("day")));
  const err = (show, text) => show && text && <div style={{ ...muted, color: AN_COLORS.danger, marginTop: 4 }}>{text}</div>;

  const neverLabel = canAddPermanent ? (
    "Never"
  ) : (
    <Tooltip title="Re-run setup on this endpoint first.">
      <span>Never</span>
    </Tooltip>
  );

  return (
    <Modal
      open={open}
      title={hostname ? `Add user on ${hostname}` : "Add user"}
      okText="Add user"
      onOk={submit}
      onCancel={() => !saving && onClose && onClose()}
      maskClosable={!saving}
      confirmLoading={saving}
      okButtonProps={{ disabled: touched && formInvalid }}
      destroyOnClose
      width={520}
    >
      <div style={{ display: "grid", gap: 16, paddingTop: 6, fontFamily: AN_FONT }}>
        <div>
          <FieldLabel required hint="lower-case letters, digits, . _ - · at most 32">
            Username
          </FieldLabel>
          <Input
            autoFocus
            value={form.account}
            placeholder="alice"
            maxLength={32}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => set("account")(e.target.value)}
            onBlur={() => form.account && setTouched(true)}
            status={touched && nameError ? "error" : undefined}
          />
          {err(touched, nameError)}
        </div>

        <div>
          <FieldLabel required>Log in with</FieldLabel>
          <Segmented
            block
            value={form.credential}
            onChange={set("credential")}
            options={[
              { value: "key", label: "SSH key" },
              { value: "password", label: "Password" },
            ]}
          />
          {form.credential === "key" ? (
            <div style={{ ...muted, marginTop: 6 }}>Uses the key Authnull holds; nothing to manage.</div>
          ) : (
            <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
              <div style={muted}>Stored in the vault and rotated automatically.</div>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: AN_COLORS.ink2 }}>
                Rotate every
                <InputNumber
                  min={1}
                  max={365}
                  precision={0}
                  value={form.intervalDays}
                  onChange={(v) => set("intervalDays")(v === null ? null : Number(v))}
                  status={touched && intervalError ? "error" : undefined}
                  style={{ width: 80 }}
                />
                days
              </span>
              {err(touched, intervalError)}
            </div>
          )}
        </div>

        <div>
          <FieldLabel required hint={`${minHours} hour${minHours === 1 ? "" : "s"} to ${maxDays} days ahead`}>
            Expires
          </FieldLabel>
          <Segmented
            block
            value={never ? "never" : "on"}
            onChange={set("expires")}
            options={[
              { value: "never", label: neverLabel, disabled: !canAddPermanent },
              { value: "on", label: "On" },
            ]}
          />
          {!never && (
            <DatePicker
              showTime={{ format: "HH:mm" }}
              format="DD MMM YYYY HH:mm"
              style={{ width: "100%", marginTop: 8 }}
              value={form.expiresAt}
              onChange={set("expiresAt")}
              disabledDate={disabledDate}
              status={touched && expiryError ? "error" : undefined}
            />
          )}
          {never ? (
            <div style={{ ...muted, marginTop: 6 }}>It stays until you remove it.</div>
          ) : (
            !expiryError &&
            form.expiresAt && (
              <div style={{ ...muted, marginTop: 6 }}>
                Removed from the machine {fmtDate(form.expiresAt.toDate())} ({relativeTime(form.expiresAt.toISOString())}).
              </div>
            )
          )}
          {err(touched || !!form.expiresAt, expiryError)}
        </div>

        <div>
          <FieldLabel hint="optional, e.g. a ticket number">Note</FieldLabel>
          <Input.TextArea
            value={form.note}
            maxLength={NOTE_MAX}
            showCount
            autoSize={{ minRows: 2, maxRows: 4 }}
            placeholder="CHG-1042 — Acme vendor maintenance"
            onChange={(e) => set("note")(e.target.value)}
          />
          {err(touched, noteError)}
        </div>

        <div style={muted}>Adding a user grants nobody access; an Endpoint Policy that names it does.</div>

        {createError && <Note tone="danger">{createError}</Note>}
      </div>
    </Modal>
  );
}

export default AddManagedAccountModal;
